import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { API_URL } from '../config'
import './AcceptInvite.css'

const AcceptInvite = () => {
  const { token } = useParams()
  const navigate = useNavigate()
  const [invitation, setInvitation] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [formData, setFormData] = useState({
    full_name: '',
    business_name: '',
    password: '',
    confirm_password: ''
  })
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    validateInvitation()
  }, [token])

  const validateInvitation = async () => {
    try {
      const response = await fetch(`${API_URL}/invitation/validate/${token}`)
      const data = await response.json()

      if (response.ok && data.success) {
        setInvitation(data.invitation)
        setFormData(prev => ({
          ...prev,
          business_name: data.invitation.business_name || ''
        }))
      } else {
        setError(data.error || 'Invalid or expired invitation')
      }
    } catch (err) {
      setError('Failed to validate invitation')
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')

    // Validation
    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters')
      return
    }

    if (formData.password !== formData.confirm_password) {
      setError('Passwords do not match')
      return
    }

    if (!formData.full_name.trim()) {
      setError('Full name is required')
      return
    }

    if (!formData.business_name.trim()) {
      setError('Business name is required')
      return
    }

    setSubmitting(true)

    try {
      const response = await fetch(`${API_URL}/invitation/accept`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          invite_token: token,
          full_name: formData.full_name,
          business_name: formData.business_name,
          password: formData.password
        })
      })

      const data = await response.json()

      if (response.ok && data.success) {
        // Store token and user info
        localStorage.setItem('token', data.access_token)
        localStorage.setItem('user', JSON.stringify(data.user))

        // Redirect to dashboard
        navigate('/dashboard')
      } else {
        setError(data.error || 'Failed to accept invitation')
      }
    } catch (err) {
      setError('Network error. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  if (loading) {
    return (
      <div className="accept-invite-page">
        <div className="invite-container">
          <div className="loading">Validating invitation...</div>
        </div>
      </div>
    )
  }

  if (error && !invitation) {
    return (
      <div className="accept-invite-page">
        <div className="invite-container">
          <div className="error-state">
            <div className="error-icon">⚠️</div>
            <h2>Invalid Invitation</h2>
            <p>{error}</p>
            <button onClick={() => navigate('/login')} className="btn-primary">
              Go to Login
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="accept-invite-page">
      <div className="invite-container">
        <div className="invite-header">
          <h1>Welcome to Exit Ready Pro</h1>
          <p className="invite-subtitle">
            You've been invited by <strong>{invitation.advisor_name}</strong> to join as a business owner.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="invite-form">
          {error && (
            <div className="alert alert-error">{error}</div>
          )}

          <div className="form-group">
            <label htmlFor="email">Email Address</label>
            <input
              type="email"
              id="email"
              value={invitation.email}
              disabled
              className="input-disabled text-base"
            />
          </div>

          <div className="form-group">
            <label htmlFor="full_name">Full Name *</label>
            <input
              type="text"
              id="full_name"
              value={formData.full_name}
              onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
              required
              placeholder="John Doe"
            />
          </div>

          <div className="form-group">
            <label htmlFor="business_name">Business Name *</label>
            <input
              type="text"
              id="business_name"
              value={formData.business_name}
              onChange={(e) => setFormData({ ...formData, business_name: e.target.value })}
              required
              placeholder="Acme Corp"
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">Password *</label>
            <input
              type="password"
              id="password"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              required
              placeholder="Minimum 6 characters"
              minLength={6}
            />
          </div>

          <div className="form-group">
            <label htmlFor="confirm_password">Confirm Password *</label>
            <input
              type="password"
              id="confirm_password"
              value={formData.confirm_password}
              onChange={(e) => setFormData({ ...formData, confirm_password: e.target.value })}
              required
              placeholder="Re-enter your password"
            />
          </div>

          <button
            type="submit"
            className="btn-submit"
            disabled={submitting}
          >
            {submitting ? 'Creating Account...' : 'Accept Invitation & Create Account'}
          </button>
        </form>

        <div className="invite-footer">
          <p>
            This invitation expires on{' '}
            <strong>{new Date(invitation.expires_at).toLocaleDateString()}</strong>
          </p>
        </div>
      </div>
    </div>
  )
}

export default AcceptInvite
